<template>
  <div class="container-fluid signup">
    <div class="row">
      <div class="col-12 col-xl-6">
        <img class="logo" src="@/assets/images/hey-marvelous.png" />
        <ValidationObserver
          @submit.prevent="signup"
          tag="form"
          class="mt-5"
          ref="form"
          v-slot="{ invalid }"
        >
          <div
            class="form-container d-flex flex-column justify-content-start align-items-center px-5"
          >
            <div class="row">
              <div class="col-md-12 mt-5">
                <h1 class="stronger">Create your affiliate account</h1>
              </div>
              <div class="col-12 mt-3 mb-4">
                <span class="subtitle">
                  Please create your account to promote Marvelous and earn
                  commission.
                </span>
              </div>
              <div class="col-md-6">
                <HMTextInput
                  rules="required"
                  autocomplete="given-name"
                  :label="$t('GENERAL.LABEL.FIRST_NAME')"
                  v-model="teacherForm.first_name"
                />
              </div>
              <div class="col-md-6">
                <HMTextInput
                  rules="required"
                  autocomplete="family-name"
                  :label="$t('GENERAL.LABEL.LAST_NAME')"
                  v-model="teacherForm.last_name"
                />
              </div>
              <div class="col-md-12 mb-3">
                <HMTextInput
                  class="mb-0"
                  rules="required"
                  type="email"
                  autocomplete="email"
                  ref="email"
                  vid="email"
                  :label="$t('GENERAL.LABEL.EMAIL')"
                  v-model.trim="teacherForm.email"
                  :infoError="emailInfo"
                />
                <span class="d-block mt-2" v-if="isUsedEmail">
                  <span class="invalid-feedback d-block">
                    {{ $t('GENERAL.ERROR.A_USER_IS_ALREADY_REGISTERED') }}
                  </span>
                  <span
                    @click="openLoginModal"
                    v-html="sanitize($t('SIGNUP.ERROR.PLEASE_CLICK_HERE_TO'))"
                  ></span>
                </span>
              </div>
              <div class="col-md-12">
                <HMTextInput
                  rules="required|min:8"
                  type="password"
                  name="password"
                  :label="$t('GENERAL.LABEL.PASSWORD')"
                  v-model="teacherForm.password"
                  autocomplete="current-password"
                />
              </div>
            </div>
            <div class="row w-100 no-gutters mb-5">
              <div class="col-md-12">
                <HMButton
                  class="w-100"
                  :class="{
                    disabled: inProgress || invalid,
                  }"
                  color="brand"
                  type="submit"
                  :loading="inProgress"
                >
                  <span class="w-100"> Create your affiliate account </span>
                </HMButton>
                <div class="d-block my-3">
                  By creating an account, I agree to the
                  <a
                    href="https://www.heymarvelous.com/affiliate-agreement"
                    target="_blank"
                    >affiliate agreement</a
                  >.
                </div>
                <HMMadeBy class="flex-grow-1 mt-5" />
              </div>
            </div>
          </div>
        </ValidationObserver>
      </div>
      <div
        class="d-none d-xl-flex col-xl-6 justify-content-center align-items-center"
      >
        <div class="cover-photo"></div>
        <!--        <iframe src="https://fast.wistia.com/embed/medias/0s6i8wcp7r?videoFoam=true&autoPlay=true&playerColor=ff69b4"-->
        <!--                height="480px"-->
        <!--                width="640px"-->
        <!--                frameborder="0"-->
        <!--        >-->
        <!--        </iframe>-->
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import Vue from 'vue';
import { debounce, isObject } from 'lodash';

import { mapGetters } from 'vuex';
import auth from '@/core/api/modules/auth';
import { register } from '@/web/services/analytics';
import { errorToMessage, scrollToFirstFormError } from '@/core/utils/errors';
import timezones from '@/web/utils/timezones';
import { getConfig } from '@/web/utils/configuration';
import StudentLogin from '@/web/components/signup/StudentLogin.vue';
import { sanitize } from '@/core/utils/utils';
import { getTrackingParams } from '@/web/utils/tracking';
import LogoutOrContinueModal from '@/web/components/signup/LogoutOrContinueModal.vue';

export default Vue.extend({
  components: { ValidationObserver },
  data() {
    return {
      teacherForm: {
        studio_name: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
      },
      inProgress: false,
      emailInfo: null,
      URLInfo: null,
      timezone: null,
      timezones,
      isUsedEmail: false,
    };
  },
  computed: {
    ...mapGetters('user', [
      'userId',
      'studentId',
      'teacher',
      'userPlans',
      'isLoggedIn',
    ]),
    ...mapGetters({
      affiliateCode: 'globals/affiliateCode',
      affiliateCoupon: 'affiliates/affiliateCoupon',
    }),
    config() {
      return getConfig();
    },
    applicationName() {
      return this.config.applicationName;
    },
    isNamastreamDomain() {
      return window.location.host.toLowerCase().indexOf('namastream') !== -1;
    },
    studioName: {
      get() {
        return this.teacherForm.studio_name || '';
      },
      set(studio_name) {
        this.$set(this.teacherForm, 'studio_name', this.slugify(studio_name));
      },
    },
  },
  methods: {
    sanitize,
    slugify(s) {
      return `${s.trim().replace(/[^0-9a-z-]/gi, '')}`;
    },
    openLoginModal() {
      this.$modal.show(StudentLogin, {});
    },
    async signup() {
      this.$refs.form.validate();
      const invalid = this.$refs.form?.flags?.invalid;
      if (invalid) {
        setTimeout(() => {
          scrollToFirstFormError();
        });
      }

      if (invalid || this.inProgress) {
        return;
      }

      this.inProgress = true;
      const data = {
        ...this.teacherForm,
        ...getTrackingParams(),
        password1: this.teacherForm.password,
        timezone: this.timezone.value,
      };

      let reCaptchaToken;
      try {
        const reCaptchaKey = process.env.VUE_APP_RECAPTCHA_KEY;
        reCaptchaToken = await window.grecaptcha.execute(reCaptchaKey, {
          action: 'teachersignup',
        });
      } catch (error) {
        this.inProgress = false;
        this.$notify({
          group: 'app',
          type: 'error',
          text: this.$t('GENERAL.ERROR.RECAPTCHA_FAILED'),
        });
      }

      try {
        await auth.registerExternalAffiliate(data);
        await this.$store.dispatch('user/login', data);
        await this.$store.dispatch('user/loadUserInfo');

        const registerData = {
          first_name: this.teacher?.first_name,
          last_name: this.teacher?.last_name,
          email: this.teacher?.email,
          user_id: this.userId,
          student_id: this.studentId,
          studio_url: this.teacher?.studio_url,
          studio_name: this.teacher?.studio_name,
        };

        register(this.userId, registerData);
        this.$trackOnPage('teacher external affiliate signed up', {
          ...registerData,
        });

        this.$router.push({ name: 'share-and-earn' });
      } catch (error) {
        console.error(error);
        const errorMessage = errorToMessage(error.response);
        if (isObject(errorMessage)) {
          this.$refs.form.setErrors(errorMessage);
          scrollToFirstFormError();
        } else {
          this.$notify({
            group: 'app',
            type: 'error',
            text: errorMessage || error,
            duration: -1, // stay ON
          });
        }
        this.inProgress = false;
      }
    },
    setLocalTimeZone() {
      try {
        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezone = this.timezones.find(
          ({ value }) => value === localTimeZone
        );
        if (timezone) this.timezone = timezone;
      } catch (error) {
        console.error(error);
      }
    },
    async redirectIfTeacher() {
      await this.$store.state.user.userInfoPromise;
      if (this.teacher) {
        this.$modal.show(
          LogoutOrContinueModal,
          {
            logoutFn: () => {
              this.$store.dispatch('user/logout');
            },
            continueFn: () => {
              this.$router.replace({ name: 'share-and-earn' });
            },
          },
          {
            clickToClose: false,
          }
        );
      }
    },
  },
  watch: {
    'teacherForm.email': debounce(async function () {
      if (this.$refs.form.fields?.email.valid) {
        try {
          await auth.verifyEmail(this.teacherForm.email);
          this.emailInfo = null;
        } catch (error) {
          this.emailInfo = this.$t('GENERAL.ERROR.ARE_YOU_SURE_THIS_EMAIL');
        }
      } else {
        this.emailInfo = null;
      }

      if (this.$refs.form.fields?.email.valid) {
        try {
          await auth.verifyNewEmail(this.teacherForm.email);
          this.isUsedEmail = false;
        } catch (error) {
          this.isUsedEmail = true;
        }
      } else {
        this.isUsedEmail = false;
      }
    }, 400),
  },
  async mounted() {
    this.redirectIfTeacher();
    if (this.isNamastreamDomain && process.env.NODE_ENV === 'production') {
      window.location.hostname = 'app.heymarvelous.com';
    }

    const handle = this.$route.query.handle || '';
    if (handle) {
      this.teacherForm.studio_name = handle;
      const query = { ...this.$route.query };
      delete query.handle;
      this.$router.replace({ query });
    } else {
      this.teacherForm.studio_name = 'ExternalAffiliate';
    }

    this.setLocalTimeZone();
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.signup {
  min-height: 100vh;
}

.logo {
  max-width: 100%;
  height: 40px;
  margin: 2rem 2rem 0 2rem;
}

.content-container {
  max-width: 780px;
}

.cover-photo {
  background-position: center center;
  background-repeat: no-repeat;
  background-image: url('~@/assets/images/affiliate-marketing.jpg');
  background-size: cover;
  width: 100%;
  height: 100%;
  min-height: 100vh;
}
h1 {
  &.stronger {
    font-weight: 700;
  }
  &.lighter {
    font-weight: 400;
  }
}
.marvelous-logo {
  width: 18px;
  height: auto;
}
.subtitle {
  line-height: 32px;
}
.form-container {
  max-width: 660px;
  margin: 0 auto;
}
</style>
